<template>
  <div id="app" class="colorScheme" :class="colorScheme">
    <div v-if="ready">
      <router-view />
      <router-view name="helpSection" />
      <!-- winning distributon modal which will be triggered by calling:
            $events.$emit('tournament-info-modal') -->
      <m-winning-distribution />
    </div>
    <c-loading-indicator v-else :show="true"></c-loading-indicator>
  </div>
</template>

<script setup lang="ts">
import 'reflect-metadata'
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import events from '@gd/event-bus'
import { useStore } from '@/store'
import { ZoneIdColorSchemeMap } from '@/model/ColorScheme'
import { State, TError } from '@/store/state'
import { ResultState } from '@/store/modules/results'
import MWinningDistribution from '@/modules/MWinningDistribution.vue'
import { TournamentHistoryState } from '@/store/modules/history'
import { ERROR_CODES } from '@/model/ErrorCodes'
import { jslog } from '@gd/jslog'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const store = useStore()
const ready = ref(false)
const { locale } = useI18n()

/**
 * Watch for error commits and send a jslog trace. The benefit of doing it in here is to use the JslogVue instance which
 * also contains the view history and the actual view path.
 *
 * A watcher is better then tracking this only in the ErrorView. Because when the logic to show the ErrorView
 * fails we wan't see it in the log.
 *
 * apart from this it has an extra benefit. Dependence on the active view which is tracked we see if the
 * errorView is shown or the pages stays on the same view ( sure, dependence on the error which is thrown ).
 *
 */
watch(
  () => store.state.error,
  (to: TError, from: TError) => {
    jslog.send({
      name: 'tournament-error',
      data: to,
    })
  }
)
// set correct view locale as lang attribute into body - required to show gameLogos
watch(
  locale,
  (to) => {
    const localeSplit = to.split('-')
    document.body.setAttribute('lang', localeSplit[0])
    document.body.setAttribute('requestcountry', localeSplit[1] || '')
  },
  { immediate: true }
)

const colorScheme = computed(() => {
  return (
    ZoneIdColorSchemeMap[store.state.zone.zoneId] ||
    ZoneIdColorSchemeMap[store.state.history.zoneId] ||
    ZoneIdColorSchemeMap[store.state.results.current.zoneId]
  )
})

onMounted(() => {
  // update balance if somebody requested it
  events.$on('update-balance', (a) => {
    store.dispatch('loadBalance')
  })
  events.$on('game-tech-mismatch', (a) => {
    jslog.send({
      name: 'tournament-technology',
      data: {
        ...a,
      },
    })
  })
  store
    .dispatch('loadUser')
    .then(() => store.dispatch('loadBalance'))
    .then(() => {
      // const user: User = store.state.user
      // TOOD vue3: remove handleInitialViewLanguage - it's already handled by @gd/i18n
      // const lang = handleInitialViewLanguage.apply(this, [user.locale])
      // locale.value = lang
      ready.value = true
    })
    .catch((err: any) => {
      if (err && err.errorCode === 401) {
        router.replace('/extern/login')
      } else {
        router.push({ path: '/error/' + (err && err.errorCode) || ERROR_CODES.UNKNOWN + '' })
        ready.value = true
      }
    })
})
</script>

<style lang="less"></style>
